import { useEffect, useState } from "react";
import { getProspectionListCount } from "../interfaces/prospectionLists";

export interface ProspectionListCount {
    totalCount: number;
    isCapped: boolean;
}

// How many counts to keep in flight. Each one runs a list's filters over the whole
// establishment collection, and a browser only opens about six sockets per host anyway:
// firing thirty at once would queue them ahead of everything else the page still needs.
const CONCURRENCY = 4;

/**
 * Resolves each list's size in its own request, in parallel, so the rows can already be
 * on screen while the numbers arrive.
 *
 * Counts are keyed by list id and land one at a time — a caller renders a placeholder for
 * any id not yet present. Ids that fail simply never appear, which leaves one row without
 * a number instead of failing the whole page.
 */
/** A list's size as a row shows it, or a placeholder while its request is still out. */
export function formatListCount(count?: ProspectionListCount, pending = "…"): string {
    if (!count) return pending;
    return `${count.totalCount.toLocaleString("fr-FR")}${count.isCapped ? "+" : ""}`;
}

export function useProspectionListCounts(ids: string[]): Record<string, ProspectionListCount> {
    const [counts, setCounts] = useState<Record<string, ProspectionListCount>>({});

    // Joined, because a fresh array on every render would restart the fetches on every render.
    const key = ids.join(",");

    useEffect(() => {
        const pending = key ? key.split(",") : [];
        if (pending.length === 0) return;

        let cancelled = false;
        let next = 0;

        const worker = async () => {
            while (!cancelled) {
                const id = pending[next++];
                if (!id) return;
                try {
                    const { totalCount, isCapped } = await getProspectionListCount(id);
                    if (!cancelled) {
                        setCounts(prev => ({ ...prev, [id]: { totalCount, isCapped } }));
                    }
                } catch (err) {
                    console.error(`Failed to count prospection list ${id}:`, err);
                }
            }
        };

        for (let i = 0; i < Math.min(CONCURRENCY, pending.length); i++) void worker();

        return () => { cancelled = true; };
    }, [key]);

    return counts;
}
